// Shared theme definitions used across the application.
// The color tokens themselves live in index.css under [data-theme="..."];
// this module only knows the theme ids, labels and how to apply them.

export const DEFAULT_THEME = "meadow";

// Where the chosen theme is saved (read before first paint in public/index.html).
// Older versions used "settings:selected".
export const THEME_KEY = "thinkloop-theme";

export const loadTheme = () => {
  try {
    return normalizeThemeId(
      localStorage.getItem(THEME_KEY) ?? localStorage.getItem("settings:selected"),
    );
  } catch {
    return DEFAULT_THEME;
  }
};

// Color theme options for settings
export const COLOR_OPTIONS = [
  { id: "meadow", label: "Meadow", mode: "light" },
  { id: "dune", label: "Dune", mode: "light" },
  { id: "ember", label: "Ember", mode: "dark" },
  { id: "frost", label: "Frost", mode: "dark" },
];

// Swatch colors for theme pickers (page background + accent)
export const THEME_SWATCHES = {
  meadow: { bg: "#f0f8f2", card: "#daf0e1", accent: "#46a177" },
  dune: { bg: "#faf8f5", card: "#f0e8da", accent: "#d97757" },
  ember: { bg: "#1d2021", card: "#3c3836", accent: "#fabd2f" },
  frost: { bg: "#2e3440", card: "#444d5f", accent: "#87bfd0" },
};

// Old ThinkLoop theme ids (global and per-note) mapped to the closest new theme,
// so saved settings and existing notes never land on a missing theme.
const LEGACY_THEME_MAP = {
  zero: "meadow",
  one: "ember",
  light: "meadow",
  two: "frost",
  three: "frost",
  four: "dune",
  five: "meadow",
  six: "dune",
  seven: "meadow",
  eight: "meadow",
  nine: "ember",
  ten: "ember",
  eleven: "ember",
  twelve: "ember",
  thirteen: "dune",
  fourteen: "frost",
  fifteen: "frost",
  sixteen: "frost",
  seventeen: "ember",
  eighteen: "ember",
  dark: "ember",
  blue: "frost",
  gray: "frost",
  cream: "dune",
  purple: "meadow",
  pink: "dune",
  skyblue: "meadow",
  sage: "meadow",
  brown: "ember",
  sunset: "ember",
  burgundy: "ember",
  forestgreen: "ember",
  gold: "dune",
  ai: "frost",
  snowleopard: "frost",
  windowsxp: "frost",
  brightred: "ember",
  gruvbox: "ember",
};

const THEME_IDS = new Set(COLOR_OPTIONS.map((o) => o.id));

export const normalizeThemeId = (id) => {
  if (THEME_IDS.has(id)) return id;
  return LEGACY_THEME_MAP[id] || DEFAULT_THEME;
};

// Per-note theme options ("default" follows the app theme)
export const NOTE_THEME_OPTIONS = [
  { id: "default", label: "Match Theme" },
  ...COLOR_OPTIONS.map(({ id, label }) => ({ id, label })),
];

export const normalizeNoteThemeId = (id) => {
  if (!id || id === "default") return "default";
  if (THEME_IDS.has(id)) return id;
  return LEGACY_THEME_MAP[id] || "default";
};

// Plain background/foreground per theme, used by the editor to keep
// pasted text colors readable against the note surface.
export const THEME_VARS = {
  meadow: { "--bg": "#f0f8f2", "--panel-bg-solid": "#e4f5e9", "--fg": "#294231" },
  dune: { "--bg": "#faf8f5", "--panel-bg-solid": "#f6efe6", "--fg": "#1c1917" },
  ember: { "--bg": "#1d2021", "--panel-bg-solid": "#373331", "--fg": "#ebdbb2" },
  frost: { "--bg": "#2e3440", "--panel-bg-solid": "#3f4758", "--fg": "#d8dee9" },
};

// Apply a theme to the document root
export const applyTheme = (themeId) => {
  const theme = normalizeThemeId(themeId);
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme =
    COLOR_OPTIONS.find((o) => o.id === theme)?.mode || "light";
  return theme;
};

// Meadow follows the time of day (morning, day, evening, night)
export const getDaypart = (date = new Date()) => {
  const h = date.getHours();
  if (h >= 5 && h < 11) return "morning";
  if (h >= 11 && h < 17) return "day";
  if (h >= 17 && h < 21) return "evening";
  return "night";
};

export const applyDaypart = () => {
  document.documentElement.dataset.daypart = getDaypart();
};
